import { useEffect, useMemo, useState } from "react";
import MoonMark from "@/components/MoonMark";
import { DEFAULTS, GROUPS } from "@/content";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminContent = () => {
  const [draft, setDraft] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/content`);
        const stored = res.ok ? await res.json() : {};
        setDraft({ ...DEFAULTS, ...stored });
      } catch {
        setError("Could not load the site text.");
        setDraft({ ...DEFAULTS });
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, []);

  const changedKeys = useMemo(() => {
    if (!draft) return [];
    return Object.keys(draft).filter((k) => draft[k] !== DEFAULTS[k]);
  }, [draft]);

  const setField = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setSaved(false);
  };

  const save = async () => {
    setError("");
    setSaved(false);
    if (!draft) return;
    setBusy(true);
    try {
      const token = localStorage.getItem("nox_admin_token") || "";
      const updates = {};
      const resets = [];
      Object.keys(DEFAULTS).forEach((k) => {
        const v = (draft[k] ?? "").trim();
        if (!v || v === DEFAULTS[k]) resets.push(k);
        else updates[k] = v;
      });
      const res = await fetch(`${API}/api/admin/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ updates, resets }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Save failed");
      setSaved(true);
    } catch (err) {
      setError(err.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded || !draft) {
    return (
      <div className="mt-10 flex items-center justify-center gap-4 py-10">
        <MoonMark className="h-6 w-6 animate-spin text-gold" style={{ animationDuration: "6s" }} />
        <span className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Summoning…</span>
      </div>
    );
  }

  return (
    <div className="mt-10" data-testid="admin-content">
      <p className="max-w-2xl text-xs leading-relaxed text-slate-500">
        Every page and section's words, in your hands — the design and layout stay untouched.
        Wrap words in *stars* to give them the gold accent, and write one highlight per line
        where the field says so. Fields back to their default are cleared on save.
      </p>

      {GROUPS.map((group) => (
        <div key={group.page} className="mt-10">
          <p className="font-display text-lg tracking-[0.15em] text-gold">{group.page}</p>
          {group.sections.map((section) => (
            <div key={section.label} className="mt-5 border border-line bg-surface p-5">
              <p className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">{section.label}</p>
              <div className="mt-4 space-y-4">
                {section.fields.map((f) => (
                  <div key={f.key}>
                    <label className="mb-2 flex items-center gap-2 font-jmono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                      {f.label}
                      {draft[f.key] !== DEFAULTS[f.key] && <span className="text-gold">● edited</span>}
                    </label>
                    {f.multiline ? (
                      <textarea
                        className="input-night min-h-[84px] resize-y text-sm"
                        value={draft[f.key] ?? ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        data-testid={`content-${f.key.replace(/\./g, "-")}`}
                      />
                    ) : (
                      <input
                        className="input-night text-sm"
                        value={draft[f.key] ?? ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        data-testid={`content-${f.key.replace(/\./g, "-")}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      {error && (
        <p className="mt-6 text-xs text-red-500" data-testid="content-error">
          {error}
        </p>
      )}
      {saved && (
        <p className="mt-6 text-xs text-emerald-400" data-testid="content-saved">
          Saved — the site now speaks these words ({changedKeys.length} edited).
        </p>
      )}

      <div className="sticky bottom-24 z-10 mt-8 flex justify-end">
        <button className="btn-night" onClick={save} disabled={busy} data-testid="content-save">
          {busy ? "Saving…" : `Save All Text${changedKeys.length ? ` (${changedKeys.length} changed)` : ""}`}
        </button>
      </div>
    </div>
  );
};

export default AdminContent;
